import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

/**
 * DRM Configuration for Communities Domain
 *
 * Provides shared DRM configuration constants and utilities
 * used across comments, posts, and groups layers.
 */

/**
 * Base DRM configuration constants for communities domain
 */
export const COMMUNITIES_DRM_CONFIG = {
  appSource: 'communities',
  entityType: 'location',
  sourceEntityType: 'media',
} as const

/**
 * Creates DRM configuration properties for media uploads in communities
 *
 * @param locationId - Location ID as a ref, computed, or getter function
 * @param initialSourceEntityId - Initial value for sourceEntityId (defaults to 'temp')
 * @returns Object containing all DRM configuration properties
 *
 * @example
 * ```typescript
 * // In a component with props.locationId
 * const drmConfig = useCommunitiesDRMConfig(() => props.locationId)
 *
 * // In a composable with reactive currentGroup
 * const drmConfig = useCommunitiesDRMConfig(
 *   computed(() => currentGroup.value?.locationId || '')
 * )
 * ```
 */
export const useCommunitiesDRMConfig = (
  locationId: MaybeRefOrGetter<string | undefined>,
  initialSourceEntityId: string = 'temp'
) => {
  const drmAppSource = COMMUNITIES_DRM_CONFIG.appSource
  const drmEntityType = COMMUNITIES_DRM_CONFIG.entityType
  const drmSourceEntityType = COMMUNITIES_DRM_CONFIG.sourceEntityType
  const drmEntityId = computed(() => toValue(locationId) || '')
  const drmSourceEntityId = ref<string>(initialSourceEntityId)

  return {
    drmAppSource,
    drmEntityType,
    drmSourceEntityType,
    drmEntityId,
    drmSourceEntityId,
  }
}

/**
 * Type for DRM configuration returned by useCommunitiesDRMConfig
 */
export type CommunitiesDRMConfig = ReturnType<typeof useCommunitiesDRMConfig>
